import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Prospect, EditProspectFormData } from './types';

interface UseProspectFormProps {
  prospect: Prospect | null;
  onClose: () => void;
  onUpdate: (id: string, updatedProspect: Partial<Prospect>) => void;
}

export const useProspectForm = ({ prospect, onClose, onUpdate }: UseProspectFormProps) => {
  const [isPending, setIsPending] = useState(false);
  
  const { register, handleSubmit, reset, formState } = useForm<EditProspectFormData>({
    defaultValues: {
      name: prospect?.name || '',
      description: prospect?.description || '',
      location: prospect?.location || '',
      industry: prospect?.industry || '',
      employee_count: prospect?.employee_count || 0,
      revenue_range: prospect?.revenue_range || '',
      website: prospect?.website || ''
    }
  });
  
  // Reset form when prospect changes
  useEffect(() => {
    if (prospect) {
      resetForm();
    }
  }, [prospect]);

  // Function to reset form to initial values
  const resetForm = () => {
    if (prospect) {
      reset({
        name: prospect.name || '',
        description: prospect.description || '',
        location: prospect.location || '',
        industry: prospect.industry || '',
        employee_count: prospect.employee_count || 0,
        revenue_range: prospect.revenue_range || '',
        website: prospect.website || ''
      });
    }
  };

  const onSubmit = async (data: EditProspectFormData) => {
    if (!prospect?.id) return;
    
    setIsPending(true);
    
    try {
      // Update the prospect
      onUpdate(prospect.id, data);
      
      // Show success message
      toast.success('Prospect updated successfully');
      
      // Reset form state to mark changes as saved
      reset(data, { keepValues: true });
      
      // Close the modal
      onClose();
    } catch (error) {
      console.error('Error updating prospect:', error);
      toast.error('Failed to update prospect');
    } finally {
      setIsPending(false);
    }
  };

  return {
    register,
    handleSubmit,
    formState,
    isPending,
    onSubmit,
    resetForm,
    isDirty: formState.isDirty,
    errors: formState.errors
  };
};
